/**
 * Vehicle glyphs: the silhouettes an operator can pick for a vehicle, drawn
 * on the map inside the state-coloured disc and in the UI beside the name.
 *
 * Same conventions as `Icon.tsx` — 24×24, stroked, no fill — so the same
 * path renders as an inline SVG in React and onto a canvas for MapLibre.
 * The state disc stays the carrier of green / yellow / red / grey; the glyph
 * only says *what* is moving, never *how* it is doing.
 */

export interface VehicleIconDef {
  id: string
  label: string
  path: string
}

export const VEHICLE_ICONS: VehicleIconDef[] = [
  {
    id: 'car',
    label: 'Car',
    path: 'M3 13l2.5-5h10l4.5 5v4H3v-4Zm0 0h17M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'van',
    label: 'Van',
    path: 'M3 7h12l6 5v5H3V7Zm12 0v5h6M3 12h12M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'bakkie',
    label: 'Bakkie / pickup',
    path: 'M3 10h8V6h5l3 4h2v7H3v-7Zm8 0h10M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'truck',
    label: 'Truck',
    path: 'M2 6h11v10H2zM13 9h4l3 3.5V16h-7M6 19.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'bus',
    label: 'Bus',
    path: 'M4 5h16v12H4V5Zm0 6h16M8 8v3M12 8v3M16 8v3M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'motorcycle',
    label: 'Motorcycle',
    path: 'M5.5 19a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm13 0a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM5.5 15.5 9 9.5h5l4.5 6M14 9.5l-2-3H9',
  },
  {
    id: 'forklift',
    label: 'Forklift',
    path: 'M3.5 17V10h3l1.5-5h5v12M16 4v13h5.5M16 10l-3-5M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'tractor',
    label: 'Tractor',
    path: 'M7 20a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm11 1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM11 13V5h4l1.5 6H19l1.5 5M11 11H8.5',
  },
  {
    id: 'trailer',
    label: 'Trailer',
    path: 'M3 7h14v9H3zM17 13h4.5M3 16h14M7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  },
  {
    id: 'boat',
    label: 'Boat',
    path: 'M3 14h18l-2.5 5H5.5L3 14ZM7 14v-4h8l3 4M10 10V7h3',
  },
  {
    id: 'asset',
    label: 'Asset / container',
    path: 'M4 8h16v9H4zM4 13h16M8 17v3M16 17v3',
  },
]

export const DEFAULT_VEHICLE_ICON = 'car'

const BY_ID = new Map(VEHICLE_ICONS.map((d) => [d.id, d]))

/** Any string from the API or an old row becomes an id a glyph exists for,
 *  so the map never asks MapLibre for an image it does not have. */
export function normalizeVehicleIcon(id: string | undefined | null): string {
  if (id && BY_ID.has(id)) return id
  return DEFAULT_VEHICLE_ICON
}

export function vehicleIconPath(id: string | undefined | null): string {
  return BY_ID.get(normalizeVehicleIcon(id))!.path
}

export function vehicleIconLabel(id: string | undefined | null): string {
  return BY_ID.get(normalizeVehicleIcon(id))!.label
}

/** Inline SVG of a vehicle glyph, same optical weight as `Icon`. */
export function VehicleIcon({
  icon,
  size = 16,
  className = '',
  strokeWidth = 1.7,
}: {
  icon: string | undefined | null
  size?: number
  className?: string
  strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      aria-hidden
    >
      <path d={vehicleIconPath(icon)} />
    </svg>
  )
}

/**
 * The glyph rasterised for MapLibre, which can only place images it has been
 * handed up front. Drawn at 2× so it stays crisp when `icon-size` scales it.
 */
export function vehicleGlyphImage(path: string, color: string, size = 48): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.scale(size / 24, size / 24)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = 2.1
  ctx.strokeStyle = color
  ctx.stroke(new Path2D(path))
  return ctx.getImageData(0, 0, size, size)
}

/** Image ids the vehicle layers use: `sv-veh-<icon>-<light|dark>`. */
export function vehicleGlyphImageId(icon: string, tone: 'light' | 'dark'): string {
  return `sv-veh-${icon}-${tone}`
}

/** Registers every glyph in both tones. Call on load and after each
 *  `setStyle`, which throws the previous style's images away. */
export function registerVehicleGlyphs(map: maplibregl.Map): void {
  for (const def of VEHICLE_ICONS) {
    const light = vehicleGlyphImageId(def.id, 'light')
    const dark = vehicleGlyphImageId(def.id, 'dark')
    if (!map.hasImage(light)) {
      map.addImage(light, vehicleGlyphImage(def.path, '#ffffff'), { pixelRatio: 2 })
    }
    if (!map.hasImage(dark)) {
      map.addImage(dark, vehicleGlyphImage(def.path, '#06080b'), { pixelRatio: 2 })
    }
  }
}
